/**
 * features/finance/Anomalies.tsx
 * ------------------------------------------------------------------
 * The second look.
 *
 * Not errors. The books balance and the controls reconcile; these are the
 * patterns an external auditor charges to look for and a bursar has no time
 * to. Every finding here may be entirely proper.
 *
 * A LIST OF QUESTIONS, NOT ACCUSATIONS. Every finding carries what would
 * make it innocent, printed alongside it rather than left to be inferred. A
 * screen that reads as an accusation gets argued with; one that reads as a
 * question gets answered.
 *
 * THE THRESHOLDS ARE ON THE PAGE. A bursar who cannot see them cannot tell
 * whether an empty screen means "nothing found" or "nothing looked for", and
 * the second is the more dangerous reading of a clean bill of health.
 *
 * WHAT CANNOT BE CHECKED YET IS LISTED TOO. Segregation of duties, shared
 * supplier bank accounts and payment tampering are the checks that matter
 * most in a school, and all of them need Payables and Suppliers. Omitting
 * them silently would imply an assurance this system has not earned.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import {
  ScanSearch, AlertTriangle, Info, ChevronDown, ChevronRight, CircleSlash,
} from 'lucide-react';
import { useEffect } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { VOUCHER_COLLECTIONS, type Voucher } from '@/lib/vouchers';
import { SUPPLIER_COLLECTION, type Supplier } from '@/lib/suppliers';
import { useGlData } from '@/features/finance/useGlData';
import { useFinanceData } from '@/features/finance/useFinanceData';
import {
  findAnomalies, clusterFindings, summarise, DEFAULT_THRESHOLDS,
  CHECK_LABEL, AWAITING_CHECKS,
  type Severity,
} from '@/lib/anomalies';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, btnGhost,
} from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';

const money = (n: number) =>
  `P${n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SEVERITY_TONE: Record<Severity, string> = {
  high: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  low: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
};

const SEVERITY_LABEL: Record<Severity, string> = {
  high: 'Worth asking about today',
  medium: 'Worth a look',
  low: 'Noted',
};

export default function Anomalies() {
  const [fy, setFy] = useState(`FY${new Date().getFullYear()}`);
  const gl = useGlData(fy);
  const fin = useFinanceData();
  const [open, setOpen] = useState<string | null>(null);
  const [severity, setSeverity] = useState<Severity | 'all'>('all');

  /* Vouchers and suppliers, for the segregation-of-duties and banking
     checks. Both modules arrived in September 2026; before that these
     checks were declared and dark. */
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  useEffect(() => onSnapshot(
    query(collection(db, VOUCHER_COLLECTIONS.vouchers)),
    (snap) => setVouchers(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Voucher)),
    () => setVouchers([]),
  ), []);
  useEffect(() => onSnapshot(
    query(collection(db, SUPPLIER_COLLECTION)),
    (snap) => setSuppliers(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Supplier)),
    () => setSuppliers([]),
  ), []);

  const findings = useMemo(() => clusterFindings(findAnomalies({
    payments: fin.payments,
    invoices: fin.invoices,
    adjustments: fin.adjustments ?? [],
    journals: gl.journals,
    unidentified: fin.unidentifiedPayments,
    vouchers,
    suppliers,
  })), [fin.payments, fin.invoices, fin.adjustments, gl.journals,
    fin.unidentifiedPayments, vouchers, suppliers]);

  const summary = useMemo(() => summarise(findings), [findings]);

  const shown = useMemo(() => {
    const list = severity === 'all' ? findings : findings.filter((f) => f.severity === severity);
    // Loudest first: a page a bursar reads top-down should start with the
    // one they would act on today.
    const rank: Record<Severity, number> = { high: 0, medium: 1, low: 2 };
    return [...list].sort((a, b) => rank[a.severity] - rank[b.severity]
      || (b.amount ?? 0) - (a.amount ?? 0));
  }, [findings, severity]);

  if (gl.loading || fin.loading) return <div className="p-8 flex justify-center"><Spinner /></div>;

  const years = Array.from({ length: 5 }, (_, i) => `FY${new Date().getFullYear() - i}`);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={ScanSearch}
        title="Anomaly Review"
        subtitle="Questions worth asking, not errors found"
        actions={(
          <select
            value={fy}
            onChange={(e) => setFy(e.target.value)}
            className="px-3 py-1.5 rounded-lg border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-800 text-sm"
          >
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
      />

      <div className="rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60 px-4 py-3 flex items-start gap-2">
        <Info size={16} className="mt-0.5 shrink-0 text-gray-500" />
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Nothing here is an error. Each is a pattern worth a question, and each says what
          would make it innocent. Looking at one and deciding it is fine is the screen working,
          not the screen being wrong.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <StatChip icon={ScanSearch} label="Findings" value={String(summary.total)} />
        <StatChip icon={AlertTriangle} label="Worth asking about today" value={String(summary.high)} />
        <StatChip icon={AlertTriangle} label="Worth a look" value={String(summary.medium)} />
        <StatChip icon={Info} label="Noted" value={String(summary.low)} />
      </div>

      <div className="flex flex-wrap gap-2">
        {(['all', 'high', 'medium', 'low'] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setSeverity(s)}
            className={`px-3 py-1.5 rounded-lg text-sm ${severity === s
              ? 'bg-primary text-white'
              : 'border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'}`}
          >
            {s === 'all' ? `Everything (${summary.total})` : SEVERITY_LABEL[s]}
          </button>
        ))}
      </div>

      {shown.length === 0 && (
        <SoftCard pad="p-8" hover={false}>
          <p className="text-sm text-center text-gray-500 dark:text-gray-400">
            Nothing found by the checks below. That is a real answer, not an empty screen —
            the thresholds are stated so you can judge it.
          </p>
        </SoftCard>
      )}

      {shown.map((f) => (
        <SoftCard key={f.id} pad="p-0" hover={false}>
          <PanelHeader
            icon={f.severity === 'high' ? AlertTriangle : ScanSearch}
            title={f.title}
            subtitle={CHECK_LABEL[f.check]}
            right={(
              <span className={`text-xs px-2 py-0.5 rounded-full ${SEVERITY_TONE[f.severity]}`}>
                {SEVERITY_LABEL[f.severity]}
              </span>
            )}
          />
          <div className="p-5 space-y-2">
            <p className="text-sm text-gray-700 dark:text-gray-200">{f.detail}</p>
            {/* PRINTED, NOT INFERRED. This is what stops the page reading
                as an accusation. */}
            <p className="text-sm text-gray-500 dark:text-gray-400">
              <span className="font-medium">Innocent if:</span> {f.innocentIf}
            </p>
            {f.amount !== undefined && f.amount > 0 && (
              <p className="text-sm tabular-nums text-gray-600 dark:text-gray-300">{money(f.amount)}</p>
            )}

            {f.members && f.members.length > 0 && (
              <>
                <button
                  type="button"
                  className={btnGhost}
                  onClick={() => setOpen(open === f.id ? null : f.id)}
                >
                  {open === f.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  {open === f.id ? 'Hide' : `Show all ${f.members.length}`}
                </button>
                {open === f.id && (
                  <ul className="max-h-72 overflow-y-auto text-sm space-y-1 pt-1">
                    {f.members.map((m) => (
                      <li key={m.id} className="text-gray-600 dark:text-gray-300">
                        {m.title}
                        {m.reference && <span className="text-gray-400 ml-2 font-mono text-xs">{m.reference}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </SoftCard>
      ))}

      {/* THE THRESHOLDS. An empty screen means nothing without them. */}
      <SoftCard pad="p-0" hover={false}>
        <PanelHeader icon={Info} title="What was looked for" />
        <div className="p-5 space-y-1 text-sm text-gray-600 dark:text-gray-300">
          <p>The same pupil and amount within {DEFAULT_THRESHOLDS.duplicateDays} days.</p>
          <p>Exact thousands from {money(DEFAULT_THRESHOLDS.roundSumFrom)}.</p>
          <p>Gaps in a receipt or invoice sequence.</p>
          <p>Journals dated {DEFAULT_THRESHOLDS.backdateDays}+ days before they were posted.</p>
          <p>
            Anything posted before {String(DEFAULT_THRESHOLDS.workStartHour).padStart(2, '0')}:00
            or after {String(DEFAULT_THRESHOLDS.workEndHour).padStart(2, '0')}:00.
          </p>
          <p>Credit notes and write-offs from {money(DEFAULT_THRESHOLDS.largeCreditFrom)}.</p>
          <p>Money unidentified for {DEFAULT_THRESHOLDS.unidentifiedDays}+ days.</p>
          <p>Pupils in credit for {DEFAULT_THRESHOLDS.creditDays}+ days.</p>
          <p>A voucher raised and approved by one person.</p>
          <p>Two suppliers sharing one bank account.</p>
          <p>
            Banking changed within {DEFAULT_THRESHOLDS.bankChangeDays} days before a payment,
            and EFTs from {money(DEFAULT_THRESHOLDS.materialAmount)} into unverified details.
          </p>
        </div>
      </SoftCard>

      <SoftCard pad="p-0" hover={false}>
        <PanelHeader
          icon={CircleSlash}
          title="What cannot be checked yet"
          subtitle="Listed because omitting them would imply an assurance this system has not earned"
        />
        <div className="p-5 space-y-1 text-sm text-gray-500 dark:text-gray-400">
          {AWAITING_CHECKS.map((c) => (
            <p key={c.label}>{c.label} — needs {c.needs}.</p>
          ))}
        </div>
      </SoftCard>
    </div>
  );
}
